import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, speak } from "@/lib/kielo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Volume2, ArrowLeft, CheckCircle2, ChevronRight, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";

function Speakable({ text, className }) {
  return (
    <button
      onClick={() => speak(text)}
      className={`inline-flex items-center gap-1.5 group ${className || ""}`}
      title="Kuuntele ääntämys"
    >
      <span>{text}</span>
      <Volume2 className="w-4 h-4 text-[#1E429F] opacity-60 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function Section({ s }) {
  if (s.type === "text")
    return (
      <div>
        <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-2">{s.heading}</h3>
        <p className="text-[#16201B] leading-relaxed">{s.body}</p>
      </div>
    );

  if (s.type === "tip")
    return (
      <div className="rounded-2xl bg-[#FDEEE5] border border-[#f6cdb4] p-5">
        <h3 className="font-serif text-lg font-semibold text-[#c9591f] mb-1.5">💡 {s.heading}</h3>
        <p className="text-[#7a3a17] leading-relaxed text-[15px]">{s.body}</p>
      </div>
    );

  if (s.type === "vocab")
    return (
      <div>
        <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-3">{s.heading}</h3>
        <div className="rounded-2xl border border-[#E2DFD7] overflow-hidden">
          {s.rows.map((r, i) => (
            <div
              key={i}
              className={`flex items-center gap-4 px-5 py-3 ${i % 2 ? "bg-white" : "bg-[#F9F8F5]"}`}
            >
              <Speakable text={r.fi} className="font-serif text-lg text-[#16201B] font-medium min-w-[42%]" />
              <span className="text-[#52635B] flex-1">{r.en}</span>
              {r.note && (
                <span className="hidden sm:block text-xs text-[#1E429F] bg-[#EDF2FE] px-2 py-1 rounded-full">
                  {r.note}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );

  if (s.type === "dialogue")
    return (
      <div>
        <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-3">{s.heading}</h3>
        <div className="space-y-3">
          {s.lines.map((l, i) => (
            <div key={i} className="rounded-2xl bg-white border border-[#E2DFD7] p-4">
              <div className="text-xs font-semibold text-[#1E429F] mb-1">{l.speaker}</div>
              <Speakable text={l.fi} className="font-serif text-lg text-[#16201B]" />
              <div className="text-sm text-[#52635B] mt-1 italic">{l.en}</div>
            </div>
          ))}
        </div>
      </div>
    );

  if (s.type === "grammar")
    return (
      <div>
        <h3 className="font-serif text-xl font-semibold text-[#0E382B] mb-2">{s.heading}</h3>
        {s.intro && <p className="text-[#16201B] leading-relaxed mb-3">{s.intro}</p>}
        <div className="rounded-2xl border border-[#E2DFD7] overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#E8F0EC]">
                {s.table.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-sm font-semibold text-[#0E382B]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.table.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-white" : "bg-[#F9F8F5]"}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`px-4 py-2.5 text-[15px] ${
                        ci === 0 ? "font-serif font-medium text-[#16201B]" : "text-[#52635B]"
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {s.note && <p className="mt-3 text-sm text-[#52635B] bg-[#F1EFEB] rounded-xl px-4 py-2.5">{s.note}</p>}
      </div>
    );

  return null;
}

export default function LessonReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, progress, completeLesson, login } = useApp();
  const { t, lang } = useI18n();
  const [lesson, setLesson] = useState(null);
  const [saving, setSaving] = useState(false);
  const done = new Set(progress?.completed_lessons || []).has(id);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/lessons/${id}`, { params: { lang } })
      .then(({ data }) => setLesson(data))
      .catch(() => navigate("/learn"));
  }, [id, navigate, lang]);

  const handleComplete = async () => {
    if (!user) {
      login();
      return;
    }
    setSaving(true);
    try {
      await completeLesson(id);
      toast.success(t("lesson.saved"));
    } catch (e) {
      toast.error(t("lesson.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  if (!lesson) return <div className="max-w-3xl mx-auto px-4 py-20 text-[#52635B]">{t("lesson.loading")}</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-[#52635B] hover:text-[#0E382B] mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("lesson.back")}
      </Link>

      <div className="flex flex-col md:flex-row gap-8">
        <article className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-3">
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-full border"
              style={
                lesson.level === "A1"
                  ? { backgroundColor: "#E8F0EC", color: "#0E382B", borderColor: "#B6D4C8" }
                  : { backgroundColor: "#EDF2FE", color: "#1E429F", borderColor: "#BFD3FE" }
              }
            >
              {lesson.level}
            </span>
            <span className="text-sm text-[#52635B]">{lesson.grammarFocus}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0E382B] tracking-tight leading-tight">
            {lesson.title}
          </h1>
          <p className="mt-3 text-lg text-[#52635B] leading-relaxed">{lesson.summary}</p>

          {lesson.image && (
            <div className="mt-6 rounded-2xl overflow-hidden h-56 sm:h-72" data-testid="lesson-hero-image">
              <img src={lesson.image} alt={lesson.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="mt-10 space-y-8">
            {lesson.sections.map((s, i) => (
              <Section key={i} s={s} />
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Button
              onClick={handleComplete}
              disabled={saving || done}
              data-testid="lesson-complete-button"
              className={`rounded-full h-12 px-6 text-base ${
                done ? "bg-[#0E5A35] hover:bg-[#0E5A35]" : "bg-[#0E382B] hover:bg-[#1C5442]"
              } text-white`}
            >
              {done ? (
                <><CheckCircle2 className="w-5 h-5 mr-1.5" /> {t("lesson.completed")}</>
              ) : (
                <>{t("lesson.complete")}</>
              )}
            </Button>
            <Link to={`/quizzes/${lesson.id}`}>
              <Button
                variant="outline"
                data-testid="lesson-start-quiz-button"
                className="rounded-full h-12 px-6 text-base border-[#E46A28] text-[#c9591f] hover:bg-[#FDEEE5] hover:text-[#c9591f]"
              >
                <ListChecks className="w-5 h-5 mr-1.5" /> {t("lesson.takeQuiz")}
              </Button>
            </Link>
          </div>
        </article>

        {/* side rail */}
        <aside className="md:w-72 shrink-0">
          <div className="md:sticky md:top-24 rounded-2xl bg-white border border-[#E2DFD7] p-6">
            <h4 className="font-serif text-lg font-semibold text-[#0E382B] mb-3">{t("lesson.keywords")}</h4>
            <div className="flex flex-wrap gap-2">
              {lesson.vocab.map((w) => (
                <button
                  key={w}
                  onClick={() => speak(w)}
                  className="text-sm bg-[#E8F0EC] text-[#0E382B] px-2.5 py-1 rounded-full border border-[#B6D4C8] hover:bg-[#d7e8df] transition-colors"
                >
                  {w}
                </button>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-[#F1EFEB]">
              <Link
                to={`/quizzes/${lesson.id}`}
                className="flex items-center justify-between text-sm font-medium text-[#1E429F] hover:underline"
              >
                {t("lesson.testKnowledge")} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
